import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import { cloudinaryConfig } from '../../../config/cloudinary';

interface ContactContent {
  heading: string;
  subheading: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  messagePlaceholder: string;
  buttonText: string;
  successMessage: string;
  leftImageUrl: string;
  formEnabled: boolean;
}

const ContactEditorPage: React.FC = () => {
  const [content, setContent] = useState<ContactContent>({
    heading: 'Contact Us',
    subheading: 'Have questions or need help? Leave us a message!',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'your.email@example.com',
    messagePlaceholder: 'Your message...',
    buttonText: 'Send',
    successMessage: "Thank you! We'll get back to you soon.",
    leftImageUrl: '',
    formEnabled: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadContactData();
  }, []);

  const loadContactData = async () => {
    try {
      const docRef = doc(db, 'landingPageContent', 'contact');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setContent(docSnap.data() as ContactContent);
      }
    } catch (error) {
      console.error('Error loading contact data:', error);
      showToast({ type: 'error', message: 'Failed to load contact data' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', message: 'Please upload an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast({ type: 'error', message: 'Image size must be less than 5MB' });
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', cloudinaryConfig.uploadPreset);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const data = await response.json();

      if (data.secure_url) {
        setContent({ ...content, leftImageUrl: data.secure_url });
        showToast({ type: 'success', message: 'Image uploaded successfully' });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast({ type: 'error', message: 'Failed to upload image' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landingPageContent', 'contact');
      await setDoc(docRef, content);
      showToast({
        type: 'success',
        message: 'Contact section saved successfully',
      });
    } catch (error) {
      console.error('Error saving contact data:', error);
      showToast({ type: 'error', message: 'Failed to save contact data' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Contact Section Editor
        </h1>
        <p className="text-gray-600">
          Edit the contact form content and settings
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Heading */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Heading
          </label>
          <input
            type="text"
            value={content.heading}
            onChange={(e) =>
              setContent({ ...content, heading: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Subheading */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subheading
          </label>
          <input
            type="text"
            value={content.subheading}
            onChange={(e) =>
              setContent({ ...content, subheading: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Left Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Left Side Image
          </label>
          {content.leftImageUrl && (
            <div className="mb-3">
              <img
                src={content.leftImageUrl}
                alt="Contact section"
                className="max-w-xs rounded-lg shadow"
              />
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploadingImage}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {uploadingImage && (
            <p className="text-sm text-gray-500 mt-2">Uploading image...</p>
          )}
        </div>

        {/* Form Placeholders */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Form Field Placeholders
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name Field Placeholder
              </label>
              <input
                type="text"
                value={content.namePlaceholder}
                onChange={(e) =>
                  setContent({ ...content, namePlaceholder: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Field Placeholder
              </label>
              <input
                type="text"
                value={content.emailPlaceholder}
                onChange={(e) =>
                  setContent({ ...content, emailPlaceholder: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Field Placeholder
              </label>
              <input
                type="text"
                value={content.messagePlaceholder}
                onChange={(e) =>
                  setContent({ ...content, messagePlaceholder: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submit Button Text
              </label>
              <input
                type="text"
                value={content.buttonText}
                onChange={(e) =>
                  setContent({ ...content, buttonText: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Success Message (shown after form submission)
          </label>
          <textarea
            value={content.successMessage}
            onChange={(e) =>
              setContent({ ...content, successMessage: e.target.value })
            }
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Form Enabled Toggle */}
        <div className="border-t pt-6">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={content.formEnabled}
              onChange={(e) =>
                setContent({ ...content, formEnabled: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">
              Enable Contact Form
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-1 ml-7">
            Uncheck to temporarily disable the contact form on the landing page
          </p>
        </div>

        {/* Save Button */}
        <div className="border-t pt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditorPage;
